import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  ResponsiveContainer, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from 'recharts';
import {
  TrendingUp, Users, PawPrint, Syringe, Stethoscope, Calendar,
  Award, Activity, BarChart3, ArrowUp, ArrowDown, Download,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { getOwners } from '../services/ownerService';
import { getPets } from '../services/petService';
import { getAppointments } from '../services/appointmentService';
import { getVaccinations } from '../services/vaccinationService';
import { getExaminations } from '../services/examinationService';
import { getVaccinationStatus } from '../utils/vaccinationStatus';

const SPECIES_COLORS = ['#6B8A45', '#557236', '#8b5cf6', '#f59e0b', '#94a3b8'];

const Statistics: React.FC = () => {
  const [period, setPeriod] = useState<'7d' | '30d' | '90d' | 'all'>('30d');

  const { data: owners = [] } = useQuery({ queryKey: ['owners'], queryFn: getOwners });
  const { data: pets = [] } = useQuery({ queryKey: ['pets'], queryFn: getPets });
  const { data: appointments = [] } = useQuery({ queryKey: ['appointments'], queryFn: getAppointments });
  const { data: vaccinations = [] } = useQuery({ queryKey: ['vaccinations'], queryFn: getVaccinations });
  const { data: examinations = [] } = useQuery({ queryKey: ['examinations'], queryFn: getExaminations });

  const now = new Date();
  const periodDays = period === '7d' ? 7 : period === '30d' ? 30 : period === '90d' ? 90 : 36500;
  const cutoff = new Date(now.getTime() - periodDays * 24 * 60 * 60 * 1000);

  const inPeriod = (date: string | undefined) => {
    if (!date) return false;
    return new Date(date) >= cutoff;
  };

  // Periyot içi sayılar
  const periodAppointments = appointments.filter((a: any) => inPeriod(a.date));
  const periodExams = examinations.filter((e: any) => inPeriod(e.date));
  const periodVaccs = vaccinations.filter((v: any) => inPeriod(v.date));

  // Önceki periyot
  const prevCutoff = new Date(cutoff.getTime() - periodDays * 24 * 60 * 60 * 1000);
  const prevAppts = appointments.filter((a: any) => {
    const d = new Date(a.date);
    return d >= prevCutoff && d < cutoff;
  });

  const apptChange = prevAppts.length === 0 ? 100 :
    Math.round(((periodAppointments.length - prevAppts.length) / prevAppts.length) * 100);

  // Tür dağılımı
  const speciesData = useMemo(() => {
    const map: Record<string, number> = {};
    pets.forEach((p: any) => {
      const s = p.species || 'other';
      const label = s === 'dog' ? 'Köpek' : s === 'cat' ? 'Kedi' : s === 'bird' ? 'Kuş' : s === 'rabbit' ? 'Tavşan' : 'Diğer';
      map[label] = (map[label] || 0) + 1;
    });
    return Object.entries(map).map(([name, value]) => ({ name, value }));
  }, [pets]);

  // En sık gelen müşteriler (randevu + muayene sayısı)
  const topOwners = useMemo(() => {
    const counts: Record<string, { name: string; visits: number; pets: number }> = {};
    owners.forEach((o: any) => {
      counts[o.id] = { name: o.name, visits: 0, pets: 0 };
    });
    pets.forEach((p: any) => {
      if (p.owner_id && counts[p.owner_id]) counts[p.owner_id].pets += 1;
    });
    examinations.forEach((e: any) => {
      const petId = e.pet_id || e.petId;
      const pet = pets.find((p: any) => p.id === petId);
      if (pet?.owner_id && counts[pet.owner_id]) counts[pet.owner_id].visits += 1;
    });
    appointments.forEach((a: any) => {
      const petId = a.pet_id || a.petId;
      const pet = pets.find((p: any) => p.id === petId);
      if (pet?.owner_id && counts[pet.owner_id]) counts[pet.owner_id].visits += 1;
    });
    return Object.values(counts)
      .filter(c => c.visits > 0 || c.pets > 0)
      .sort((a, b) => (b.visits + b.pets) - (a.visits + a.pets))
      .slice(0, 5);
  }, [owners, pets, examinations, appointments]);

  // Aylık trend (son 6 ay)
  const monthlyTrend = useMemo(() => {
    const months: { ay: string; Muayene: number; Randevu: number; Aşı: number }[] = [];
    for (let i = 5; i >= 0; i--) {
      // Ayın 29-31'inde setMonth() ay atlatır; her zaman ayın 1'inden hesapla
      const now = new Date();
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const monthLabel = d.toLocaleDateString('tr-TR', { month: 'short' });
      const filterFn = (item: any) => {
        const date = item.date;
        if (!date) return false;
        return date.startsWith(monthKey);
      };
      months.push({
        ay: monthLabel,
        Muayene: examinations.filter(filterFn).length,
        Randevu: appointments.filter(filterFn).length,
        Aşı: vaccinations.filter(filterFn).length,
      });
    }
    return months;
  }, [appointments, examinations, vaccinations]);

  // Randevu tipleri dağılımı
  const apptTypeData = useMemo(() => {
    const types: Record<string, number> = {
      'Kontrol': 0, 'Aşı': 0, 'Ameliyat': 0, 'Acil': 0, 'Diğer': 0,
    };
    periodAppointments.forEach((a: any) => {
      const label = a.type === 'checkup' ? 'Kontrol' :
        a.type === 'vaccination' ? 'Aşı' :
        a.type === 'surgery' ? 'Ameliyat' :
        a.type === 'emergency' ? 'Acil' : 'Diğer';
      types[label] = (types[label] || 0) + 1;
    });
    return Object.entries(types).filter(([, v]) => v > 0).map(([name, value]) => ({ name, value }));
  }, [periodAppointments]);

  const handleExportExcel = () => {
    const wb = XLSX.utils.book_new();

    // Sheet 1: Randevular
    const apptRows = (appointments as any[]).map(a => ({
      Tarih: a.date,
      Saat: a.time || '',
      'Hasta Adı': a.pet?.name || '',
      'Sahip Adı': a.pet?.owner?.name || '',
      Tür: a.type === 'checkup' ? 'Kontrol' : a.type === 'vaccination' ? 'Aşı' :
            a.type === 'surgery' ? 'Ameliyat' : a.type === 'emergency' ? 'Acil' : 'Diğer',
      Durum: a.status === 'completed' ? 'Tamamlandı' : a.status === 'cancelled' ? 'İptal' :
              a.status === 'no-show' ? 'Gelmedi' : 'Planlandı',
      Notlar: a.notes || '',
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(apptRows), 'Randevular');

    // Sheet 2: Muayeneler
    const examRows = (examinations as any[]).map(e => ({
      Tarih: e.date,
      'Hasta Adı': e.pet?.name || '',
      'Sahip Adı': e.pet?.owner?.name || '',
      Semptomlar: e.symptoms || '',
      Teşhis: e.diagnosis || '',
      Tedavi: e.treatment || '',
      Reçete: e.prescription || '',
      Notlar: e.notes || '',
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(examRows), 'Muayeneler');

    // Sheet 3: Aşılar
    const vaccRows = (vaccinations as any[]).map(v => ({
      Tarih: v.date,
      'Hasta Adı': v.pet?.name || '',
      'Sahip Adı': v.pet?.owner?.name || '',
      'Aşı Adı': v.name || '',
      'Sonraki Tarih': v.next_due_date || v.nextDueDate || '',
      Durum: (() => {
        const st = getVaccinationStatus(v, vaccinations as any[]);
        return st === 'completed' ? 'Tamamlandı' : st === 'overdue' ? 'Gecikti' : st === 'approaching' ? 'Yaklaşıyor' : 'Bekliyor';
      })(),
      Notlar: v.notes || '',
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(vaccRows), 'Aşılar');

    // Sheet 4: Hastalar
    const petRows = (pets as any[]).map(p => ({
      'Protokol No': p.protocolNo || '',
      'Hasta Adı': p.name,
      Tür: p.species === 'dog' ? 'Köpek' : p.species === 'cat' ? 'Kedi' : p.species === 'bird' ? 'Kuş' : p.species === 'rabbit' ? 'Tavşan' : 'Diğer',
      Irk: p.breed || '',
      'Yaş (ay)': p.age || '',
      'Kilo (kg)': p.weight || '',
      Cinsiyet: p.gender === 'male' ? 'Erkek' : p.gender === 'female' ? 'Dişi' : '',
      'Sahip Adı': p.owner?.name || '',
      'Sahip Tel': p.owner?.phone || '',
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(petRows), 'Hastalar');

    const dateStr = new Date().toLocaleDateString('tr-TR').replace(/\./g, '-');
    XLSX.writeFile(wb, `KlinikVet-Rapor-${dateStr}.xlsx`);
  };

  const periods: { key: typeof period; label: string }[] = [
    { key: '7d', label: 'Son 7 gün' },
    { key: '30d', label: 'Son 30 gün' },
    { key: '90d', label: 'Son 90 gün' },
    { key: 'all', label: 'Tümü' },
  ];

  const statCards = [
    {
      label: 'Toplam Müşteri',
      value: owners.length,
      icon: Users,
      color: 'teal',
      sub: `${pets.length} hasta`,
    },
    {
      label: 'Randevular',
      value: periodAppointments.length,
      icon: Calendar,
      color: 'cyan',
      sub: `Önceki: ${prevAppts.length}`,
      change: apptChange,
    },
    {
      label: 'Muayeneler',
      value: periodExams.length,
      icon: Stethoscope,
      color: 'rose',
      sub: 'Bu dönem',
    },
    {
      label: 'Aşılar',
      value: periodVaccs.length,
      icon: Syringe,
      color: 'purple',
      sub: 'Bu dönem',
    },
  ];

  return (
    <div className="page-stack">
      {/* Header */}
      <div className="page-head">
        <div>
          <h1 className="page-title flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center text-white shadow-md shadow-teal-300/40">
              <BarChart3 className="w-5 h-5" />
            </span>
            İstatistikler
          </h1>
          <p className="page-subtitle">Kliniğin performans raporları</p>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
        {/* Excel export */}
        <button
          onClick={handleExportExcel}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[12px] font-semibold transition-colors"
          style={{ backgroundColor: 'rgba(61,139,125,0.1)', color: '#557236', border: '1px solid rgba(61,139,125,0.25)' }}
          title="Tüm verileri Excel'e aktar"
        >
          <Download className="w-3.5 h-3.5" />
          Excel'e Aktar
        </button>

        {/* Dönem seçici */}
        <div className="flex gap-1.5 rounded-xl p-1" style={{ backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)' }}>
          {periods.map(p => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                period === p.key
                  ? 'bg-[#557236] text-white shadow-md shadow-teal-300/40'
                  : 'text-[#8A8676] hover:bg-[#F0EBE1]'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        </div>
      </div>

      {/* Stat kartları */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((s, i) => {
          const Icon = s.icon;
          const colorMap: any = {
            teal: { bg: 'bg-[#E8EFD9]', text: 'text-[#557236]' },
            cyan: { bg: 'bg-[#E8EFD9]', text: 'text-[#557236]' },
            rose: { bg: 'bg-[#FAECE7]', text: 'text-[#C97B5E]' },
            purple: { bg: 'bg-[#F0EBE1]', text: 'text-[#8A8676]' },
          };
          const c = colorMap[s.color];
          return (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="dribbble-panel p-5"
            >
              <div className="flex items-start justify-between mb-3">
                <div className={`w-10 h-10 rounded-xl ${c.bg} flex items-center justify-center`}>
                  <Icon className={`w-5 h-5 ${c.text}`} />
                </div>
                {s.change !== undefined && (
                  <span className={`inline-flex items-center gap-0.5 text-xs font-bold ${
                    s.change >= 0 ? 'text-emerald-600' : 'text-red-500'
                  }`}>
                    {s.change >= 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                    {Math.abs(s.change)}%
                  </span>
                )}
              </div>
              <p className="text-xs text-[#8A8676] font-medium mb-1">{s.label}</p>
              <p className="text-2xl font-bold text-[#2B2B2B] leading-none">{s.value}</p>
              <p className="text-xs text-[#B0A99A] mt-1.5">{s.sub}</p>
            </motion.div>
          );
        })}
      </div>

      {/* Trend grafiği */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="dribbble-panel p-5"
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="section-title mb-0">Aylık Trend</h2>
            <p className="text-xs text-[#8A8676]">Son 6 ayda yapılan işlemler</p>
          </div>
          <TrendingUp className="w-5 h-5 text-[#557236]" />
        </div>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={monthlyTrend}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
            <XAxis dataKey="ay" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} allowDecimals={false} />
            <Tooltip contentStyle={{ borderRadius: 10, border: '1px solid #e5e7eb', fontSize: 12 }} />
            <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
            <Line type="monotone" dataKey="Randevu" stroke="#06b6d4" strokeWidth={2.5} dot={{ r: 4 }} />
            <Line type="monotone" dataKey="Muayene" stroke="#f43f5e" strokeWidth={2.5} dot={{ r: 4 }} />
            <Line type="monotone" dataKey="Aşı" stroke="#8b5cf6" strokeWidth={2.5} dot={{ r: 4 }} />
          </LineChart>
        </ResponsiveContainer>
      </motion.div>

      {/* 2'li grid: Tür dağılımı + Randevu türleri */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Hayvan türleri */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="dribbble-panel p-5"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="section-title mb-0">Hayvan Türleri</h2>
              <p className="text-xs text-[#8A8676]">{pets.length} hasta üzerinden</p>
            </div>
            <PawPrint className="w-5 h-5 text-amber-500" />
          </div>
          {speciesData.length === 0 ? (
            <p className="text-center text-[#B0A99A] py-12 text-sm">Henüz hasta kaydı yok</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={speciesData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={3}
                >
                  {speciesData.map((_, i) => (
                    <Cell key={i} fill={SPECIES_COLORS[i % SPECIES_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 10, border: '1px solid #e5e7eb', fontSize: 12 }} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </motion.div>

        {/* Randevu türleri */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="dribbble-panel p-5"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="section-title mb-0">Randevu Türleri</h2>
              <p className="text-xs text-[#8A8676]">Bu dönem</p>
            </div>
            <Activity className="w-5 h-5 text-[#557236]" />
          </div>
          {apptTypeData.length === 0 ? (
            <p className="text-center text-[#B0A99A] py-12 text-sm">Bu dönem randevu yok</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={apptTypeData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 10, border: '1px solid #e5e7eb', fontSize: 12 }} />
                <Bar dataKey="value" fill="#06b6d4" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </motion.div>
      </div>

      {/* En aktif müşteriler */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="dribbble-panel p-5"
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="section-title mb-0">En Aktif Müşteriler</h2>
            <p className="text-xs text-[#8A8676]">Toplam ziyaret + hayvan sayısı</p>
          </div>
          <Award className="w-5 h-5 text-amber-500" />
        </div>

        {topOwners.length === 0 ? (
          <p className="text-center text-[#B0A99A] py-12 text-sm">Henüz aktif müşteri yok</p>
        ) : (
          <div className="space-y-2">
            {topOwners.map((o, i) => {
              const total = o.visits + o.pets;
              const max = topOwners[0].visits + topOwners[0].pets;
              const percent = (total / max) * 100;
              const medal = i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`;
              return (
                <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-[#F7F4ED] border border-[#F0EBE1]">
                  <div className="w-10 text-center">
                    <span className="text-lg">{medal}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-semibold text-[#2B2B2B] truncate">{o.name}</p>
                      <span className="text-xs text-[#8A8676] ml-2 shrink-0">
                        {o.visits} ziyaret · {o.pets} hayvan
                      </span>
                    </div>
                    <div className="w-full h-1.5 bg-[#E8E2D4] rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-[#557236] to-[#8FA862] rounded-full"
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default Statistics;
